import type { IResource } from "@gettactic/api";

export interface RoomSelectionInternalState {
  /** Every room of the reservation, in pick order — the room the reserve card is
   *  showing included. */
  rooms: IResource[];
  isPickingRoom: boolean;
  /** True from the moment a second room is taken until the selection is cleared.
   *  It outlives dropping back to one room on purpose — see the flag's use in
   *  MapReserveCard — and cannot be inferred from `rooms` afterwards. */
  isMultiRoomSession: boolean;
}

export type RoomSelectionAction =
  | { type: "addRoom"; room: IResource; maxRooms: number }
  | { type: "removeRoom"; roomId: string }
  | { type: "startPicking" }
  | { type: "cancelPicking" }
  | { type: "clearRooms" };

export const initialRoomSelectionState: RoomSelectionInternalState = {
  rooms: [],
  isPickingRoom: false,
  isMultiRoomSession: false
};

/**
 * Pure state machine behind the multi-room selection. Picking is a single-shot arm:
 * taking a room — or hitting the cap — disarms the map again. A room the reservation
 * already holds is the exception: it adds nothing, so it must not consume the pick.
 */
export const roomSelectionReducer = (
  state: RoomSelectionInternalState,
  action: RoomSelectionAction
): RoomSelectionInternalState => {
  switch (action.type) {
    case "addRoom": {
      const isAlreadyPicked = state.rooms.some(
        (room) => room.id === action.room.id
      );
      if (isAlreadyPicked) {
        return state;
      }
      if (state.rooms.length >= action.maxRooms) {
        return { ...state, isPickingRoom: false };
      }
      const rooms = [...state.rooms, action.room];
      return {
        rooms,
        isPickingRoom: false,
        // Derived here, at the action, rather than watched on `rooms` by a consumer:
        // a selection that restarts on another room clears and re-adds in one go, and
        // the empty state in between is never rendered.
        isMultiRoomSession: state.isMultiRoomSession || rooms.length > 1
      };
    }
    case "removeRoom": {
      const rooms = state.rooms.filter((room) => room.id !== action.roomId);
      if (rooms.length === state.rooms.length) {
        return state;
      }
      return { ...state, rooms };
    }
    case "startPicking":
      return { ...state, isPickingRoom: true };
    case "cancelPicking":
      return { ...state, isPickingRoom: false };
    case "clearRooms":
      return initialRoomSelectionState;
  }
};
